import type { Component, LayoutDataItems, AdditionalLayoutProperties } from '@wix/thunderbolt-becky-types'
import { envRefs } from '@wix/thunderbolt-catharsis'
import _, { get, identity, isUndefined, mapValues, merge, set } from 'lodash'
import { createCssCompNode } from '../cssCompNode'
import type { FeatureRefs } from '../cssFeatures.types'
import type { BreakpointsVariantsToLayoutsMap, BreakpointsVariantsToSelectorsMap } from './responsiveLayout.types'
import { clearFutileOverridesFromSelectorsMap } from '../../utils/futileCssOptimizer'
import { EMPTY_ARRAY } from '../../utils/constants'
import { getContainerLayouts } from '../../utils/responsiveLayoutUtils'
import { layoutDataItemToCss } from '@wix/thunderbolt-commons'

const ONE_CELL_GRID_DISPLAY_FLEX_COMPONENTS = new Set(['Container', 'Section'])

const CONTAINER_SELECTORS = ['component-one-cell-grid', 'container']

const COMPONENT_DEFAULT_CSS_PATH = ['default', '', 'component']
const ITEM_DEFAULT_CSS_PATH = ['default', '', 'item']
const PATHS = {
	POSITION: [...COMPONENT_DEFAULT_CSS_PATH, 'position'],
	ASPECT_RATIO: [...COMPONENT_DEFAULT_CSS_PATH, 'aspect-ratio'],
	DISPLAY: [...COMPONENT_DEFAULT_CSS_PATH, 'display'],
	TOP: [...COMPONENT_DEFAULT_CSS_PATH, 'top'],
	COMPONENT_DISPLAY: [...COMPONENT_DEFAULT_CSS_PATH, '--comp-display'],
	TOP_VARIABLE: [...ITEM_DEFAULT_CSS_PATH, '--top'],
}

const variablize = (variable: string, fallback?: string | number) =>
	typeof fallback !== 'undefined' ? `var(${variable},${fallback})` : `var(${variable})`

export const VARIABLES = {
	DISPLAY: '--display',
	CONTAINER_DISPLAY: '--container-display',
	LAYOUT_DISPLAY: '--l_display',
	ASPECT_RATIO: '--aspect-ratio',
	TOP: '--top',
	IS_STICKY: '--is-sticky',
	FORCE_AUTO: '--force-auto',
	WIX_ADS_TOP_HEIGHT: '--wix-ads-height',
	STICKY_OFFSET: '--sticky-offset',
} as const

const MOBILE_VARIANT_ID = 'MOBILE-VARIANT'

const CONTAINER_DISPLAY = variablize(VARIABLES.LAYOUT_DISPLAY, variablize(VARIABLES.CONTAINER_DISPLAY))
const COMPONENT_DEFAULTS = {
	TOP: `calc(${variablize(VARIABLES.IS_STICKY, 0)} * (${variablize(VARIABLES.TOP)} + ${variablize(
		VARIABLES.STICKY_OFFSET
	)}))`,
	DISPLAY: variablize(VARIABLES.LAYOUT_DISPLAY, variablize(VARIABLES.DISPLAY, 'block')),
	ASPECT_RATIO: `1/${variablize(VARIABLES.ASPECT_RATIO)}`,
	COMPONENT_DISPLAY: 'unset',
}
const SAFARI_STICKY_FIX_VAR = { 'will-change': 'var(--safari-sticky-fix)' }
const EXPERIMENTAL_SAFARI_STICKY_FIX_VAR = { transform: 'var(--experimental-safari-sticky-fix)' }
const ASPECT_RATIO_PATH = ['component', VARIABLES.ASPECT_RATIO]

const applyAspectRatio = (css: BreakpointsVariantsToSelectorsMap) => {
	for (const breakpointId in css) {
		const breakpointCss = css[breakpointId]
		for (const variantsKey in breakpointCss) {
			const variantCss = breakpointCss[variantsKey]
			const aspectRatio = get(variantCss, ASPECT_RATIO_PATH)
			const componentHeight = get(variantCss, ['component', 'height'])
			// use auto aspect ratio if no aspect ratio is set or if the component height is set
			if (isUndefined(aspectRatio) && !isUndefined(componentHeight)) {
				set(variantCss, ASPECT_RATIO_PATH, 'auto')
			}
		}
	}

	set(css, PATHS.ASPECT_RATIO, COMPONENT_DEFAULTS.ASPECT_RATIO)
}

const isClassicLayout = (breakpointToLayouts: BreakpointsVariantsToLayoutsMap) =>
	Object.values(breakpointToLayouts)
		.flatMap((x) => Object.values(x).flatMap((y) => y))
		.some((layout) => layout.type === 'ClassicComponentLayout')

const filterVariantInCaseOfClassic = (
	variantInSpecificBreakpoint: { [p: string]: Array<LayoutDataItems> },
	isMobileView: boolean
): { [p: string]: Array<LayoutDataItems> } =>
	isMobileView && variantInSpecificBreakpoint[MOBILE_VARIANT_ID]
		// We don't want to apply .device-mobile-optimized class because it'll override the css reaching from css mappers
		? { '': variantInSpecificBreakpoint[MOBILE_VARIANT_ID] }
		: _.omit(variantInSpecificBreakpoint, [MOBILE_VARIANT_ID])

const responsiveLayoutCompNode = createCssCompNode('responsiveLayout', 'responsiveLayout', {
	getDependencies: (component: Component, refs: FeatureRefs<'responsiveLayout'>) =>
		component.layoutQuery
			? {
				breakpointsVariantsToLayouts: refs.compLayouts(component),
				layoutFlags: refs.compLayoutFlags(component),
				renderScrollSnap: envRefs.renderScrollSnap,
				globalRenderSticky: envRefs.renderSticky,
				componentRenderSticky: envRefs.compsToRenderSticky(component.id),
				isMobileView: envRefs.isMobileView,
				experiments: envRefs.experiments,
				childrenIsPinned: (
					(ONE_CELL_GRID_DISPLAY_FLEX_COMPONENTS.has(component.componentType) && component.components) ||
						EMPTY_ARRAY
				).map(refs.isPinned),
				vhScalingFactor: envRefs.vhScalingFactor,
			}
			: null,
	toViewItem: (component, deps) => {
		if (!deps) {
			return null
		}

		const {
			breakpointsVariantsToLayouts,
			renderScrollSnap,
			globalRenderSticky,
			componentRenderSticky,
			experiments,
			childrenIsPinned,
			isMobileView,
			layoutFlags,
			vhScalingFactor,
		} = deps

		if (!breakpointsVariantsToLayouts || !layoutFlags) {
			return { css: {}, shouldOmitWrapperLayers: false, isDynamicSlotsExperimentOn: false }
		}

		const isDynamicSlotsExperimentOn = !!experiments['specs.thunderbolt.dynamicSlots']
		const containerLayouts = getContainerLayouts(breakpointsVariantsToLayouts)

		const hasClassicLayout = isClassicLayout(breakpointsVariantsToLayouts)

		const shouldDeclareHeaderHeightFlex = !!experiments[
			'specs.thunderbolt.one_cell_grid_display_flex_header_height'
		]

		const isOneCellGridDisplayBlock =
			!!experiments['specs.thunderbolt.one_cell_grid_display_block'] && !childrenIsPinned.some(identity)
		const isOneCellGridDisplayFlex =
			!isOneCellGridDisplayBlock &&
			!!experiments['specs.thunderbolt.one_cell_grid_display_flex'] &&
			ONE_CELL_GRID_DISPLAY_FLEX_COMPONENTS.has(component.componentType) &&
			!childrenIsPinned.some(identity)
		const options: AdditionalLayoutProperties = {
			hasOverflow: layoutFlags.hasOverflow,
			shouldOmitWrapperLayers: layoutFlags.shouldOmitWrapperLayers,
			renderScrollSnap,
			renderSticky: globalRenderSticky || !!componentRenderSticky,
			isOneCellGridDisplayFlex,
			isOneCellGridDisplayBlock,
			shouldDeclareHeaderHeightFlex,
			vhScalingFactor: vhScalingFactor ? vhScalingFactor : undefined,
		}

		let hasAspectRatioSomewhere = false
		const css = mapValues(breakpointsVariantsToLayouts, (variantInSpecificBreakpoint, breakpointId) => {
			return mapValues(
				filterVariantInCaseOfClassic(variantInSpecificBreakpoint, isMobileView),
				(layouts, variantId) => {
					const itemsCss = layouts.map((layout: LayoutDataItems) =>
						layoutDataItemToCss(layout, {
							...options,
							spx: get(breakpointsVariantsToLayouts, [breakpointId, '', '0', 'spx']),
						})
					)
					const mergedCss = merge({}, ...itemsCss)

					// assert default position
					if (
						!mergedCss?.item?.position &&
						breakpointId === 'default' &&
						variantId === '' &&
						!layouts.find((l) => l.type === 'MeshItemLayout')
					) {
						set(mergedCss, ['item', 'position'], 'relative')
					}

					const aspectRatio = mergedCss?.component?.[VARIABLES.ASPECT_RATIO]
					if (aspectRatio) {
						hasAspectRatioSomewhere = true
					}

					if (!hasClassicLayout) {
						for (const selector of CONTAINER_SELECTORS) {
							if (mergedCss?.[selector]?.display) {
								mergedCss[selector][VARIABLES.CONTAINER_DISPLAY] = mergedCss[selector].display
								mergedCss[selector].display = CONTAINER_DISPLAY
							}
						}

						const position = mergedCss?.item?.position
						if (position === 'sticky') {
							mergedCss.item[VARIABLES.IS_STICKY] = 1

							const removeStickySafariFixExperiment = !!experiments[
								'specs.thunderbolt.removeSafariStickyFix'
							]
							const safariStickyFixExperiment = !!experiments['specs.thunderbolt.safariStickyFix']

							if (!removeStickySafariFixExperiment) {
								// jira.wixpress.com/browse/LAYOUT-441
								mergedCss['next-siblings'] = {
									...SAFARI_STICKY_FIX_VAR,
								}
							}

							if (safariStickyFixExperiment) {
								mergedCss['next-siblings'] = {
									...EXPERIMENTAL_SAFARI_STICKY_FIX_VAR,
								}
							}
						}
					}

					return mergedCss
				}
			)
		})

		if (hasAspectRatioSomewhere) {
			applyAspectRatio(css)
		}

		if (!hasClassicLayout) {
			if (isUndefined(get(css, PATHS.COMPONENT_DISPLAY))) {
				set(css, PATHS.COMPONENT_DISPLAY, COMPONENT_DEFAULTS.COMPONENT_DISPLAY)
			}

			// allow leaf components to specify their own display via css variable
			if (!containerLayouts.length) {
				set(css, PATHS.DISPLAY, COMPONENT_DEFAULTS.DISPLAY)
			}
		}

		return {
			css,
			shouldOmitWrapperLayers: layoutFlags.shouldOmitWrapperLayers,
			isDynamicSlotsExperimentOn,
			isMobileView,
		}
	},
})

export const viewerResponsiveLayoutCompNode = createCssCompNode('responsiveLayout', 'responsiveLayout', {
	getDependencies: responsiveLayoutCompNode.getDependencies,
	toViewItem: (component, deps) => {
		const result = responsiveLayoutCompNode.toViewItem(component, deps)
		if (!result) {
			return null
		}
		const { css, shouldOmitWrapperLayers, isDynamicSlotsExperimentOn, isMobileView } = result
		return {
			css: clearFutileOverridesFromSelectorsMap(css),
			shouldOmitWrapperLayers,
			isDynamicSlotsExperimentOn,
			isMobileView,
		}
	},
})

export const responsiveLayout = (isEditor: boolean) =>
	isEditor ? responsiveLayoutCompNode : viewerResponsiveLayoutCompNode
